import {
  EXCEL_IMPORT_UPLOAD_START,
  EXCEL_IMPORT_UPLOAD_SUCCESS,
  EXCEL_IMPORT_UPLOAD_FAILURE,
  EXCEL_IMPORT_PREVIEW_START,
  EXCEL_IMPORT_PREVIEW_SUCCESS,
  EXCEL_IMPORT_PREVIEW_FAILURE,
  EXCEL_IMPORT_EXECUTE_START,
  EXCEL_IMPORT_EXECUTE_SUCCESS,
  EXCEL_IMPORT_EXECUTE_FAILURE,
  EXCEL_IMPORT_STATUS_UPDATE,
  EXCEL_IMPORT_PROGRESS_UPDATE,
  EXCEL_IMPORT_COMPLETED,
  EXCEL_IMPORT_CANCELLED,
  EXCEL_IMPORT_HISTORY_FETCH_START,
  EXCEL_IMPORT_HISTORY_FETCH_SUCCESS,
  EXCEL_IMPORT_HISTORY_FETCH_FAILURE,
  EXCEL_IMPORT_TEMPLATES_FETCH_START,
  EXCEL_IMPORT_TEMPLATES_FETCH_SUCCESS,
  EXCEL_IMPORT_TEMPLATES_FETCH_FAILURE,
  EXCEL_IMPORT_TEMPLATE_SAVE_START,
  EXCEL_IMPORT_TEMPLATE_SAVE_SUCCESS,
  EXCEL_IMPORT_TEMPLATE_SAVE_FAILURE,
  EXCEL_IMPORT_FIELDS_FETCH_START,
  EXCEL_IMPORT_FIELDS_FETCH_SUCCESS,
  EXCEL_IMPORT_FIELDS_FETCH_FAILURE,
  EXCEL_IMPORT_SUGGESTIONS_FETCH_START,
  EXCEL_IMPORT_SUGGESTIONS_FETCH_SUCCESS,
  EXCEL_IMPORT_SUGGESTIONS_FETCH_FAILURE,
  EXCEL_IMPORT_VALIDATION_RULES_FETCH_START,
  EXCEL_IMPORT_VALIDATION_RULES_FETCH_SUCCESS,
  EXCEL_IMPORT_VALIDATION_RULES_FETCH_FAILURE,
  EXCEL_IMPORT_AUDIT_LOGS_FETCH_START,
  EXCEL_IMPORT_AUDIT_LOGS_FETCH_SUCCESS,
  EXCEL_IMPORT_AUDIT_LOGS_FETCH_FAILURE,
  EXCEL_IMPORT_RESET_STATE,
  EXCEL_IMPORT_SET_CURRENT_STEP,
  EXCEL_IMPORT_UPDATE_MAPPING,
  EXCEL_IMPORT_UPDATE_OPTIONS
} from '../actions/constants';

const initialState = {
  // UI State
  currentStep: 1,
  isLoading: false,
  error: null,
  
  // Upload State
  uploadProgress: 0,
  uploadedFile: null,
  fileAnalysis: {
    fileId: null,
    fileName: '',
    fileSize: 0,
    entityType: '',
    headers: [],
    sampleData: [],
    totalRows: 0,
    suggestedMappings: []
  },
  
  // Mapping State
  mappingConfiguration: {
    fieldMappings: [],
    headerRow: 1,
    startRow: 2,
    updateExisting: false,
    createMissing: true
  },
  
  // Preview State
  previewData: [],
  validationErrors: [],
  previewStatistics: {
    totalRows: 0,
    validRows: 0,
    invalidRows: 0,
    warnings: 0
  },
  
  // Import Execution State
  activeImports: {}, // importId -> import details
  importProgress: {}, // importId -> progress percentage
  importResults: {}, // importId -> final results
  
  // Templates State
  templates: [],
  templatesLoading: false,
  templateSaving: false,
  
  // Entity Fields State
  entityFields: {},
  fieldsLoading: false,
  
  // Suggestions State
  suggestions: [],
  suggestionsLoading: false,
  
  // Validation Rules State
  validationRules: {
    validators: [],
    businessRules: [],
    fields: []
  },
  validationRulesLoading: false,
  
  // History State
  importHistory: [],
  historyPagination: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    hasNext: false,
    hasPrev: false
  },
  historyLoading: false,
  
  // Audit Logs State
  auditLogs: [],
  auditLogsPagination: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    hasNext: false,
    hasPrev: false
  },
  auditLogsLoading: false,
  
  // Options State
  importOptions: {
    batchSize: 100,
    skipErrors: false,
    sendNotification: true,
    saveAsTemplate: false,
    templateName: ''
  }
};

const excelImportReducer = (state = initialState, action) => {
  switch (action.type) {
    // Upload Actions
    case EXCEL_IMPORT_UPLOAD_START:
      return {
        ...state,
        isLoading: true,
        error: null,
        uploadProgress: 0
      };
      
    case EXCEL_IMPORT_UPLOAD_SUCCESS:
      return {
        ...state,
        isLoading: false,
        error: null,
        uploadProgress: 100,
        fileAnalysis: {
          fileId: action.payload.fileId,
          fileName: action.payload.fileName,
          fileSize: action.payload.fileSize,
          entityType: action.payload.entityType,
          headers: action.payload.headers,
          sampleData: action.payload.sampleData,
          totalRows: action.payload.totalRows,
          suggestedMappings: action.payload.suggestedMappings
        },
        mappingConfiguration: {
          ...state.mappingConfiguration,
          fieldMappings: action.payload.suggestedMappings || []
        }
      };
      
    case EXCEL_IMPORT_UPLOAD_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: action.payload,
        uploadProgress: 0
      };
      
    // Preview Actions
    case EXCEL_IMPORT_PREVIEW_START:
      return {
        ...state,
        isLoading: true,
        error: null
      };
      
    case EXCEL_IMPORT_PREVIEW_SUCCESS:
      return {
        ...state,
        isLoading: false,
        error: null,
        previewData: action.payload.previewData,
        validationErrors: action.payload.validationErrors,
        previewStatistics: action.payload.statistics
      };
      
    case EXCEL_IMPORT_PREVIEW_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: action.payload
      };
      
    // Execute Actions
    case EXCEL_IMPORT_EXECUTE_START:
      return {
        ...state,
        isLoading: true,
        error: null
      };
      
    case EXCEL_IMPORT_EXECUTE_SUCCESS:
      return {
        ...state,
        isLoading: false,
        error: null,
        activeImports: {
          ...state.activeImports,
          [action.payload.importId]: {
            importId: action.payload.importId,
            status: action.payload.status,
            startedAt: new Date().toISOString()
          }
        },
        importProgress: {
          ...state.importProgress,
          [action.payload.importId]: 0
        }
      };
      
    case EXCEL_IMPORT_EXECUTE_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: action.payload
      };
      
    // Status and Progress Updates
    case EXCEL_IMPORT_STATUS_UPDATE:
      return {
        ...state,
        activeImports: {
          ...state.activeImports,
          [action.payload.importId]: {
            ...state.activeImports[action.payload.importId],
            status: action.payload.status,
            results: action.payload.results,
            errors: action.payload.errors
          }
        }
      };
      
    case EXCEL_IMPORT_PROGRESS_UPDATE:
      return {
        ...state,
        importProgress: {
          ...state.importProgress,
          [action.payload.importId]: action.payload.progress.percentage || 0
        }
      };
      
    case EXCEL_IMPORT_COMPLETED:
      return {
        ...state,
        activeImports: {
          ...state.activeImports,
          [action.payload.importId]: {
            ...state.activeImports[action.payload.importId],
            status: 'COMPLETED',
            completedAt: new Date().toISOString()
          }
        },
        importResults: {
          ...state.importResults,
          [action.payload.importId]: action.payload.results
        },
        importProgress: {
          ...state.importProgress,
          [action.payload.importId]: 100
        }
      };
      
    case EXCEL_IMPORT_CANCELLED:
      return {
        ...state,
        activeImports: {
          ...state.activeImports,
          [action.payload.importId]: {
            ...state.activeImports[action.payload.importId],
            status: 'CANCELLED',
            cancelledAt: new Date().toISOString()
          }
        }
      };
      
    // History Actions
    case EXCEL_IMPORT_HISTORY_FETCH_START:
      return {
        ...state,
        historyLoading: true,
        error: null
      };
      
    case EXCEL_IMPORT_HISTORY_FETCH_SUCCESS:
      return {
        ...state,
        historyLoading: false,
        error: null,
        importHistory: action.payload.imports,
        historyPagination: action.payload.pagination
      };
      
    case EXCEL_IMPORT_HISTORY_FETCH_FAILURE:
      return {
        ...state,
        historyLoading: false,
        error: action.payload
      };
      
    // Templates Actions
    case EXCEL_IMPORT_TEMPLATES_FETCH_START:
      return {
        ...state,
        templatesLoading: true,
        error: null
      };
      
    case EXCEL_IMPORT_TEMPLATES_FETCH_SUCCESS:
      return {
        ...state,
        templatesLoading: false,
        error: null,
        templates: action.payload.templates
      };
      
    case EXCEL_IMPORT_TEMPLATES_FETCH_FAILURE:
      return {
        ...state,
        templatesLoading: false,
        error: action.payload
      };
      
    case EXCEL_IMPORT_TEMPLATE_SAVE_START:
      return {
        ...state,
        templateSaving: true,
        error: null
      };
      
    case EXCEL_IMPORT_TEMPLATE_SAVE_SUCCESS:
      return {
        ...state,
        templateSaving: false,
        error: null,
        templates: [
          ...state.templates.filter(t => t._id !== action.payload._id),
          action.payload
        ]
      };
      
    case EXCEL_IMPORT_TEMPLATE_SAVE_FAILURE:
      return {
        ...state,
        templateSaving: false,
        error: action.payload
      };
      
    // Fields Actions
    case EXCEL_IMPORT_FIELDS_FETCH_START:
      return {
        ...state,
        fieldsLoading: true,
        error: null
      };
      
    case EXCEL_IMPORT_FIELDS_FETCH_SUCCESS:
      return {
        ...state,
        fieldsLoading: false,
        error: null,
        entityFields: {
          ...state.entityFields,
          [action.payload.entityType]: action.payload.fields
        }
      };
      
    case EXCEL_IMPORT_FIELDS_FETCH_FAILURE:
      return {
        ...state,
        fieldsLoading: false,
        error: action.payload
      };
      
    // Suggestions Actions
    case EXCEL_IMPORT_SUGGESTIONS_FETCH_START:
      return {
        ...state,
        suggestionsLoading: true,
        error: null
      };
      
    case EXCEL_IMPORT_SUGGESTIONS_FETCH_SUCCESS:
      return {
        ...state,
        suggestionsLoading: false,
        error: null,
        suggestions: action.payload.suggestions
      };
      
    case EXCEL_IMPORT_SUGGESTIONS_FETCH_FAILURE:
      return {
        ...state,
        suggestionsLoading: false,
        error: action.payload
      };
      
    // Validation Rules Actions
    case EXCEL_IMPORT_VALIDATION_RULES_FETCH_START:
      return {
        ...state,
        validationRulesLoading: true,
        error: null
      };
      
    case EXCEL_IMPORT_VALIDATION_RULES_FETCH_SUCCESS:
      return {
        ...state,
        validationRulesLoading: false,
        error: null,
        validationRules: {
          validators: action.payload.validators,
          businessRules: action.payload.businessRules,
          fields: action.payload.fields
        }
      };
      
    case EXCEL_IMPORT_VALIDATION_RULES_FETCH_FAILURE:
      return {
        ...state,
        validationRulesLoading: false,
        error: action.payload
      };
      
    // Audit Logs Actions
    case EXCEL_IMPORT_AUDIT_LOGS_FETCH_START:
      return {
        ...state,
        auditLogsLoading: true,
        error: null
      };
      
    case EXCEL_IMPORT_AUDIT_LOGS_FETCH_SUCCESS:
      return {
        ...state,
        auditLogsLoading: false,
        error: null,
        auditLogs: action.payload.logs,
        auditLogsPagination: action.payload.pagination
      };
      
    case EXCEL_IMPORT_AUDIT_LOGS_FETCH_FAILURE:
      return {
        ...state,
        auditLogsLoading: false,
        error: action.payload
      };
      
    // UI State Actions
    case EXCEL_IMPORT_SET_CURRENT_STEP:
      return {
        ...state,
        currentStep: action.payload
      };
      
    case EXCEL_IMPORT_UPDATE_MAPPING:
      return {
        ...state,
        mappingConfiguration: {
          ...state.mappingConfiguration,
          ...action.payload
        }
      };
      
    case EXCEL_IMPORT_UPDATE_OPTIONS:
      return {
        ...state,
        importOptions: {
          ...state.importOptions,
          ...action.payload
        }
      };
      
    case EXCEL_IMPORT_RESET_STATE:
      return {
        ...initialState,
        // Keep some state that shouldn't be reset
        entityFields: state.entityFields,
        templates: state.templates
      };
      
    default:
      return state;
  }
};

export default excelImportReducer;
